import { useState } from "react";
import {
  addJourneyActions,
  deleteJourneyActions,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  getFlowByAutomationId,
  createFlow,
  updateFlow,
  getAutomationsBybrandId,
  getBrandSegments,
  getUserData,
  getBrands,
  getBrandById,
  getWhatsappNumbers,
  getCampaigns,
  getEmailSources,
  getWhatsappTemplate,
  getSmsTemplates,
  tesFilter,
  testQueue,
  getDynamicTables,
  getDynamicTableFields,
  getDynamicTableFieldsById,
  getSmsSenders,
  createSmsTemplate,
  createWhatsappTemplate,
  createSmsSender,
  getAutomationById,
  tryGetToken,
  createInforUToken,
  getIntegrations,
  getSingleWhatsappTemplate,
  getQuery,
  getQueryResults,
  executeQueryWithJoinData,
} from "./useAutomationAPI";

//cache area?
const CACHE_TTL_MS = 15 * 60 * 1000;
const whatsappTplCache = new Map();
const smsTplCache = new Map();
const emailTplCache = new Map();
const dynamicTablesCache = new Map();
const dynamicTableFieldsCache = new Map();
const brandsCache = new Map();

export default function useAutomations() {
  const [userData, setUserData] = useState(null);
  const [brandSegments, setBrandSegments] = useState([]);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsSenders, setSmsSenders] = useState([]);
  const [emails, setEmails] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [currentAutomation, setCurrentAutomation] = useState(null);
  const [dynamicTables, setDynamicTables] = useState(null);
  const [dynamicTableFields, setDynamicTableFields] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brands, setBrands] = useState([]);

  const loadBrands = async ({ force = false } = {}) => {
    const CACHE_KEY = "all_brands";
    const cached = brandsCache.get(CACHE_KEY);

    if (
      cached &&
      !force &&
      Date.now() - cached.ts < CACHE_TTL_MS &&
      Array.isArray(cached.list) &&
      cached.list.length
    ) {
      setBrands(cached.list);
      setLoading(false);
      return cached.list;
    }

    try {
      setLoading(true);
      const res = await getBrands();

      const list = Array.isArray(res.data) ? res.data : res?.data?.data || [];

      brandsCache.set(CACHE_KEY, { list, ts: Date.now() });
      setBrands(list);
      setLoading(false);
      return list;
    } catch (err) {
      console.error("Error in loadBrands:", err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleGetBrands = async () => {
    return loadBrands({ force: false });
  };

  const handleGetBrandById = async (brandId) => {
    if (!brandId) throw new Error("brandId is required");

    try {
      setLoading(true);
      const response = await getBrandById(brandId);
      setLoading(false);
      return response?.data ?? null;
    } catch (error) {
      setError(error);
      setLoading(false);
      throw error;
    }
  };

  const loadWhatsappTemplates = async (brandId, { force = false } = {}) => {
    if (!brandId) throw new Error("brandId is required");
    /* ---------- fast-path: serve from cache when allowed ---------- */
    const cached = whatsappTplCache.get(brandId);
    if (
      cached &&
      !force &&
      Date.now() - cached.ts < CACHE_TTL_MS &&
      Array.isArray(cached.list) &&
      cached.list.length
    ) {
      setWhatsappTemplates({ data: { Data: { List: cached.list } } });
      return cached.list;
    }

    /* ---------- go to the server ---------- */
    try {
      setLoading(true); // twin of the other helpers
      const response = await getWhatsappTemplate(brandId);
      const list = response?.data?.Data?.List ?? [];
      // update React state + cache
      setWhatsappTemplates(response);
      whatsappTplCache.set(brandId, { list, ts: Date.now() });
      setLoading(false);
      return list;
    } catch (err) {
      setError(err); // same error surface as peers
      throw err; // let callers handle if needed
    } finally {
      setLoading(false);
    }
  };

  const loadEmailTemplates = async (brandId, { force = false } = {}) => {
    if (brandId) {
      const cached = emailTplCache.get(brandId);
      if (
        cached &&
        !force &&
        Date.now() - cached.ts < CACHE_TTL_MS &&
        Array.isArray(cached.list) &&
        cached.list.length
      ) {
        setEmails({ data: cached.list });
        console.log(cached.list);
        setLoading(false);
        return cached.list;
      }

      try {
        setLoading(true);
        const res = await getEmailSources(brandId);
        const list = res.data ?? []; // adapt once, store flat
        setEmails({ data: list });
        emailTplCache.set(brandId, { list, ts: Date.now() });
        setLoading(false);
        return list;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }
  };

  const loadSmsTemplates = async (brandId, { force = false } = {}) => {
    if (!brandId) throw new Error("brandId is required");

    const cached = smsTplCache.get(brandId);
    console.log(cached);
    console.log(!force);

    if (cached && !force && Date.now() - cached.ts < CACHE_TTL_MS) {
      setSmsTemplates({ data: cached.list });
      setLoading(false);
      console.log("sms fetched from cache");
      return cached.list;
    }

    try {
      setLoading(true);
      const res = await getSmsTemplates(brandId); // already imported
      console.log(res);

      const list = res?.data ?? [];
      console.log(list);

      smsTplCache.set(brandId, { list, ts: Date.now() });
      setSmsTemplates(res);
      console.log("sms fetched from server");
      return list;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadDynamicTables = async (brandId, { force = false } = {}) => {
    if (!brandId) throw new Error("brandId is required");

    const cached = dynamicTablesCache.get(brandId);
    console.log(cached);
    console.log(!force);

    if (cached && !force && Date.now() - cached.ts < CACHE_TTL_MS) {
      setDynamicTables({ data: cached.list });
      setLoading(false);
      console.log("dynamic tables fetched from cache");
      return cached.list;
    }

    try {
      setLoading(true);
      const res = await getDynamicTables(brandId);
      console.log(res);

      const list = res?.data ?? [];
      console.log(list);

      dynamicTablesCache.set(brandId, { list, ts: Date.now() });
      setDynamicTables(res);
      console.log("dynamic tables fetched from server");
      return list;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleGetDynamicTables = async (brandId) => {
    if (brandId) {
      return loadDynamicTables(brandId, { force: false });
    }
  };

  const loadDynamicTableFields = async (brandId, { force = false } = {}) => {
    if (!brandId) throw new Error("brandId is required");

    const cached = dynamicTableFieldsCache.get(brandId);
    if (
      cached &&
      !force &&
      Date.now() - cached.ts < CACHE_TTL_MS &&
      Array.isArray(cached.list) &&
      cached.list.length
    ) {
      setDynamicTableFields({ data: cached.list });
      setLoading(false);
      return cached.list;
    }

    try {
      setLoading(true);
      const res = await getDynamicTableFields(brandId);
      const list = res ?? [];

      dynamicTableFieldsCache.set(brandId, { list, ts: Date.now() });
      setDynamicTableFields({ data: list });
      return list;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleGetDynamicTableFields = async (brandId) => {
    if (brandId) {
      return loadDynamicTableFields(brandId, { force: false });
    }
  };

  const handleGetDynamicTableFieldsById = async (brandId, tableId) => {
    try {
      setLoading(true);
      const response = await getDynamicTableFieldsById(brandId, tableId);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshDynamicTableFields = (id) =>
    loadDynamicTableFields(id, { force: true });

  const handleGetUserData = async () => {
    try {
      setLoading(true);
      const response = await getUserData();
      setUserData(response);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleGetInforUToken = async (brandId) => {
    try {
      setLoading(true);
      const response = await tryGetToken(brandId);
      setLoading(false);
      return response;
    } catch (error) {
      setError(error);
      setLoading(false);
      throw error;
    }
  };

  const handleCreateInforUToken = async (brandId, userId, username, apiKey) => {
    try {
      setLoading(true);
      const response = await createInforUToken(
        brandId,
        userId,
        username,
        apiKey
      );
      setLoading(false);
      return response;
    } catch (error) {
      setError(error);
      setLoading(false);
      throw error;
    }
  };

  const handleGetBrandSegments = async (brandId) => {
    try {
      const response = await getBrandSegments(brandId);
      setBrandSegments(response);
      return response;
    } catch (error) {
      setError(error);
      return error;
    }
  };

  const handleGetWhatsappNumbers = async (brandId) => {
    try {
      const response = await getWhatsappNumbers(brandId);
      setWhatsappNumbers(response);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const handleCreateSmsSender = async (brandId, sender) => {
    try {
      const response = await createSmsSender(brandId, sender);
      setSmsSenders({ ...smsSenders, sender });
      return response;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const handleGetSmsSenders = async (brandId) => {
    try {
      const sendersList = await getSmsSenders(brandId);
      setSmsSenders(sendersList);
      return sendersList;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const handleCreateSmsTemplate = async (brandId, templateData) => {
    try {
      console.log(templateData);

      const response = await createSmsTemplate(brandId, templateData);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const handleCreateWhatsappTemplate = async (brandId, templateData) => {
    try {
      const response = await createWhatsappTemplate(brandId, templateData);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const handleGetWhatsappTemplates = async (brandId) => {
    if (brandId) {
      return loadWhatsappTemplates(brandId, { force: false }); // uses cache
    }
  };

  const handleGetSingleWhatsappTemplate = async (templateId) => {
    try {
      console.log(templateId);

      const response = await getSingleWhatsappTemplate(templateId);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const handleGetEmails = async (brandId) => {
    if (brandId) {
      return loadEmailTemplates(brandId, { force: false }); // uses cache
    }
  };

  const handleGetSmsTemplates = async (brandId) => {
    if (brandId) {
      return loadSmsTemplates(brandId, { force: false }); // uses cache
    }
  };

  const refreshEmailTemplates = (id) => loadEmailTemplates(id, { force: true });
  const refreshWhatsappTemplates = (id) =>
    loadWhatsappTemplates(id, { force: true });
  const refreshSmsTemplates = (id) => loadSmsTemplates(id, { force: true });

  const handleGetCampaigns = async (brandId) => {
    try {
      const response = await getCampaigns(brandId);
      setCampaigns(response);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const handleGetIntegrations = async (brandId, campaignId) => {
    try {
      const response = await getIntegrations(brandId, campaignId);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    }
  };

  const handleCreateAutomationSubmit = async (formData, brandId, onSuccess) => {
    try {
      setLoading(true);

      // Get userData correctly
      let currentUserData = userData;
      if (!currentUserData) {
        currentUserData = await handleGetUserData();
      }

      const userId = currentUserData?.data?.userId;

      // Use brandId from formData (dropdown selection) as priority
      const finalBrandId = formData.brandId || brandId;

      console.log(currentUserData);
      console.log(finalBrandId);

      if (!userId || !finalBrandId) {
        throw new Error("Missing required user or brand information");
      }

      const response = await createAutomation(formData, finalBrandId, userId);
      console.log(response);

      const journey = await handleCreateFlow(response.Id);
      console.log(journey);

      if (response.IsSuccess === false) {
        throw new Error(response.Message);
      }

      setAutomations((prev) => [...prev, response]);

      if (onSuccess) {
        onSuccess(journey);
      }

      return response;
    } catch (error) {
      console.error("Submit Error:", error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlow = async (automationId) => {
    console.log("id: ", automationId);

    try {
      const payload = {
        AutomationId: automationId,
        NodesJson: "",
        EdgesJson: "",
        ViewportJson: "",
      };
      console.log("payload: ", payload);

      const response = await createFlow(payload);
      console.log("response:", response);

      return response;
    } catch (error) {
      console.error("Submit Error:", error);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleEditJourneyAutomation = async (id, payload) => {
    try {
      setLoading(true);
      console.log("payload", payload);

      const response = await updateFlow(id, payload);
      /* const response = await editJourneyAutomation(payload); */
      console.log("response", response);

      if (response && response.Id) {
        // Update local state
        setAutomations((prev) =>
          prev.map((auto) => (auto.Id === payload.Id ? response : auto))
        );
      } else {
        console.error("Received invalid response:", response);
        // Handle the invalid response accordingly, e.g., show an error message
      }

      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleEditAutomation = async (id, payload, brandId, userId) => {
    try {
      setLoading(true);
      const response = await updateAutomation(id, payload, brandId, userId);

      // Update local state
      setAutomations((prev) =>
        prev.map((auto) => {
          if (auto && auto.Id === id) {
            return response && response.Id ? response : auto;
          }
          return auto;
        })
      );

      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleAddJourneyActions = async (payload) => {
    try {
      setLoading(true);
      const response = await addJourneyActions(payload);

      // Update current automation if actions were added to it
      if (currentAutomation?.Id === payload.automationId) {
        setCurrentAutomation((prev) => ({
          ...prev,
          Actions: [...(prev.Actions || []), response],
        }));
      }

      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJourneyActions = async (actionId, automationId) => {
    try {
      setLoading(true);
      await deleteJourneyActions(actionId);

      // Update current automation if action was deleted from it
      if (currentAutomation?.Id === automationId) {
        setCurrentAutomation((prev) => ({
          ...prev,
          Actions: prev.Actions.filter((action) => action.Id !== actionId),
        }));
      }
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJourneyAutomation = async (automationId) => {
    try {
      setLoading(true);
      await deleteAutomation(automationId);

      // Update local state
      setAutomations((prev) => prev.filter((auto) => auto.Id !== automationId));

      // Clear current automation if it was deleted
      if (currentAutomation?.Id === automationId) {
        setCurrentAutomation(null);
      }
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleGetJourneyAutomations = async (brandId) => {
    try {
      setLoading(true);
      /* const response = await getJourneyAutomations(brandId); */
      const response = await getAutomationsBybrandId(brandId);
      setAutomations(response);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleGetJourneyAutomationById = async (automationId) => {
    try {
      setLoading(true);
      const response = await getAutomationById(automationId);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleGetFlow = async (automationId) => {
    try {
      setLoading(true);
      /* const response = await getJourneyAutomation(automationId); */
      const response = getFlowByAutomationId(automationId);
      setCurrentAutomation(response);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleTestQueue = async (brandId) => {
    try {
      setLoading(true);
      /* const response = await getJourneyAutomation(automationId); */
      const response = testQueue(brandId);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleGetQuery = async (brandId, conditions) => {
    try {
      setLoading(true);
      const response = getQuery(brandId, conditions);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleGetQueryResults = async (brandId, conditions) => {
    try {
      setLoading(true);
      const response = await getQueryResults(brandId, conditions);
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handdleExecuteEnhancedQuery = async (
    brandId,
    conditions,
    joinConfigurations = null
  ) => {
    setLoading(true);
    try {
      const response = await executeQueryWithJoinData(
        brandId,
        conditions,
        joinConfigurations
      );
      return response;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    userData,
    brandSegments,
    automations,
    currentAutomation,
    isLoading,
    error,
    dynamicTables,
    dynamicTableFields,
    whatsappNumbers,
    whatsappTemplates,
    smsTemplates,
    smsSenders,
    campaigns,
    emails,
    brands,
    handleGetInforUToken,
    handleCreateInforUToken,
    handleTestQueue,
    handleGetEmails,
    handleGetCampaigns,
    handleGetWhatsappNumbers,
    handleCreateSmsSender,
    handleGetSmsSenders,
    handleCreateSmsTemplate,
    handleCreateWhatsappTemplate,
    handleGetSingleWhatsappTemplate,
    handleGetWhatsappTemplates,
    handleGetSmsTemplates,
    handleGetUserData,
    handleGetBrands,
    handleGetBrandById,
    handleGetBrandSegments,
    handleGetIntegrations,
    handleCreateAutomationSubmit,
    handleEditJourneyAutomation,
    handleEditAutomation,
    handleAddJourneyActions,
    handleDeleteJourneyActions,
    handleDeleteJourneyAutomation,
    handleGetJourneyAutomationById,
    handleGetJourneyAutomations,
    handleGetFlow,
    handleGetDynamicTables,
    handleGetDynamicTableFields,
    handleGetDynamicTableFieldsById,
    refreshDynamicTableFields,
    handleGetQuery,
    handleGetQueryResults,
    handdleExecuteEnhancedQuery,
    refreshWhatsappTemplates,
    refreshEmailTemplates,
    refreshSmsTemplates,
    setLoading,
  };
}
